import { useCallback, useEffect, useState } from 'react';
import { batch } from 'react-redux';

import * as St from './StorePage.styles';

import RetailStoresMap from '~/UI/components/organisms/RetailStoresMap';
import SavedEstimateList from '~/UI/components/organisms/SavedEstimateList';
import SavedEstimateListSkeleton from '~/UI/components/organisms/SavedEstimateList/SavedEstimateList.skeleton.component';
import FullPageLoader from '~/UI/components/templates/FullPageLoader';
import { LOCAL_STORAGE_KEY } from '~/core/constants';
import { getLocalData, removeLocalData } from '~/core/helpers/storageHelper';
import { useEstimateContext } from '~/legacy/easy-estimator-page/context/estimateContext';
import { useStoresContext } from '~/legacy/easy-estimator-page/context/storesContext';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectShowListOfEstimates,
  updateIsNewEstimate,
} from '~/redux/slices/estimate';
import { updateZipcode } from '~/redux/slices/zipcode';
import RetailStores from '~organisms/RetailStores';
import StoreChangeModal from '~organisms/StoreChangeModal';
import StoreSearch from '~templates/StoreSearch';

const StorePage = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { isFetchingEstimate } = useEstimateContext();
  const [isZipModalVisible, setIsZipModalVisible] = useState(false);
  const [newZipCode, setNewZipCode] = useState<string>();

  const showListOfEstimates = useAppSelector(selectShowListOfEstimates);
  const isEstimateCodeSwitch =
    getLocalData(LOCAL_STORAGE_KEY.ESTIMATE_CODE_SWITCH) === 'true';
  const onConfirmChangingZipCodeFromModal = useCallback(() => {
    if (newZipCode) {
      dispatch(updateZipcode(newZipCode));
    }

    setIsZipModalVisible(false);
  }, [dispatch, newZipCode]);

  const onCancelChangingZipCodeFromModal = () => {
    setIsZipModalVisible(false);
  };
  const {
    getters: { googleMapsApiKey, googleMapsMapId, zipCodeLocation, stores },
  } = useStoresContext();

  const showMap =
    Boolean(googleMapsApiKey) &&
    Boolean(googleMapsMapId) &&
    Boolean(zipCodeLocation) &&
    !!stores.length;

  useEffect(() => {
    if (isEstimateCodeSwitch && showListOfEstimates) {
      removeLocalData(LOCAL_STORAGE_KEY.ESTIMATE_CODE_SWITCH);
    }
  }, [isEstimateCodeSwitch, showListOfEstimates]);

  if (isFetchingEstimate) {
    return <FullPageLoader />;
  } else if (showListOfEstimates) {
    return <SavedEstimateList />;
  } else if (isEstimateCodeSwitch) {
    return <SavedEstimateListSkeleton />;
  }

  return (
    <St.Container>
      <StoreSearch
        onSubmitWithNonEmptyDrawingArea={(newZipcode) => {
          setNewZipCode(newZipcode);
          setIsZipModalVisible(true);
        }}
        onSubmitWithEmptyDrawingArea={(newZipcode) => {
          batch(() => {
            dispatch(updateZipcode(newZipcode));
            dispatch(updateIsNewEstimate(true));
          });
        }}
      >
        <RetailStores />
      </StoreSearch>
      {showMap && (
        <St.MapContainer>
          <RetailStoresMap />
        </St.MapContainer>
      )}
      <StoreChangeModal
        newZipCode={newZipCode}
        isVisible={isZipModalVisible}
        handleClose={onCancelChangingZipCodeFromModal}
        handleChange={onConfirmChangingZipCodeFromModal}
      />
    </St.Container>
  );
};

export default StorePage;
